import React, { useState, useEffect } from 'react';
import { Container, Card, Alert, Table, Spinner, Accordion, Image, Badge, Form, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getCastaways } from '../api/castaways';
import { Castaway } from '../types';
import '../App.css';

interface EpisodeScore {
  tribeImmunityWins: number;
  tribeRewardWins: number;
  individualRewardWins: number;
  rightSideOfVote: number;
  individualImmunityWins: number;
  votesAgainst: number;
  idolObtained: number;
  advantageObtained: number;
  playedIdolAdvantage: number;
  advantageAbandoned: number;
  idolAbandoned: number;
  eliminationPick: number;
  shotInDarkWin: number;
  coinFlipWin?: number;
  finalPlacement: number;
  votedOut: boolean;
  involuntaryEvacuation?: boolean;
  voluntaryExit?: boolean;
  notes?: string;
}

interface AllScores {
  [episode: string]: {
    [castawayId: string]: EpisodeScore;
  };
}

interface CastawayInventory {
    idols: number;
    advantages: number;
}

interface AllInventory {
    [castawayId: string]: CastawayInventory;
}

interface UserInfo {
  userName: string;
  email: string;
  leagueId: string;
  paymentMethod: string;
  survivorName?: string;
}

interface CastawayScoreSummary {
  id: number;
  name: string;
  imageUrl: string;
  totalScore: number;
  breakdown: { [episode: string]: { [category: string]: number } };
  episodeNotes: { [episode: string]: string };
  votedOutEpisode: number | null;
  involuntaryEvacuationEpisode: number | null;
  voluntaryExitEpisode: number | null;
  finalPlacement: number;
  inventory: CastawayInventory;
  isDrafted: boolean;
}

const scoringRubric = {
  tribeImmunityWins: 1,
  tribeRewardWins: 1,
  individualRewardWins: 2,
  rightSideOfVote: 1,
  individualImmunityWins: 1, // Points are calculated by player count in Admin and stored as the value
  votesAgainst: -0.5,
  idolObtained: 4,
  advantageObtained: 2,
  playedIdolAdvantage: 2,
  advantageAbandoned: -2,
  idolAbandoned: -3,
  eliminationPick: 3,
  shotInDarkWin: 4,
  coinFlipWin: 4,
  finalPlacement: { 1: 25, 2: 10, 3: 5 },
};

const calculateCastawayScores = (
  allCastaways: Castaway[],
  allScores: AllScores,
  allInventory: AllInventory,
  userTeam: number[],
  eliminationPicks: { [userName: string]: { [episode: string]: number } },
  userInfo: UserInfo | null
): CastawayScoreSummary[] => {
  const userTeamSet = new Set(userTeam);
  const summaries = allCastaways.map(castaway => {
    let totalScore = 0;
    const breakdown: { [episode: string]: { [category: string]: number } } = {};
    const episodeNotes: { [episode: string]: string } = {};
    let votedOutEpisode: number | null = null;
    let involuntaryEvacuationEpisode: number | null = null;
    let voluntaryExitEpisode: number | null = null;
    let finalPlacement = 0;

    const episodeNumbers = Object.keys(allScores).map(Number).sort((a, b) => a - b);

    let dynamicIdols = 0;
    let dynamicAdvantages = 0;

    episodeNumbers.forEach(episodeNum => {
      const episode = String(episodeNum);
      const scores = allScores[episode]?.[castaway.id] || (allScores[episode] as any)?.[String(castaway.id)];
      if (scores) {
        if (scores.notes) {
          episodeNotes[episode] = scores.notes;
        }

        const isEp1 = episodeNum === 1;
        let episodeTotal = 0;
        const episodeBreakdown: { [category: string]: number } = {};

        // Track live dynamic inventory from scoring events across all episodes (including Ep 1)
        dynamicIdols += (scores.idolObtained || 0);
        dynamicIdols += (scores.coinFlipWin || 0); // Winning coin flip awards an idol
        dynamicIdols -= (scores.idolAbandoned || 0);

        dynamicAdvantages += (scores.advantageObtained || 0);
        dynamicAdvantages -= (scores.advantageAbandoned || 0);

        // Deduct when played (if playedIdolAdvantage is recorded)
        if (scores.playedIdolAdvantage) {
          if (dynamicIdols > 0) {
            dynamicIdols = Math.max(0, dynamicIdols - scores.playedIdolAdvantage);
          } else if (dynamicAdvantages > 0) {
            dynamicAdvantages = Math.max(0, dynamicAdvantages - scores.playedIdolAdvantage);
          }
        }

        const addScore = (cat: keyof EpisodeScore, points: number, count: number) => {
          if (count > 0 || count < 0) {
            const score = isEp1 ? 0 : points * count;
            episodeTotal += score;
            episodeBreakdown[cat] = (episodeBreakdown[cat] || 0) + score;
          }
        };

        addScore('tribeImmunityWins', scoringRubric.tribeImmunityWins, scores.tribeImmunityWins);
        addScore('tribeRewardWins', scoringRubric.tribeRewardWins, scores.tribeRewardWins);
        addScore('individualRewardWins', scoringRubric.individualRewardWins, scores.individualRewardWins);
        addScore('rightSideOfVote', scoringRubric.rightSideOfVote, scores.rightSideOfVote);
        if(scores.individualImmunityWins !== 0) addScore('individualImmunityWins', 1, scores.individualImmunityWins);
        addScore('votesAgainst', scoringRubric.votesAgainst, scores.votesAgainst);
        addScore('idolObtained', scoringRubric.idolObtained, scores.idolObtained);
        addScore('advantageObtained', scoringRubric.advantageObtained, scores.advantageObtained);
        addScore('playedIdolAdvantage', scoringRubric.playedIdolAdvantage, scores.playedIdolAdvantage);
        addScore('advantageAbandoned', scoringRubric.advantageAbandoned, scores.advantageAbandoned);
        addScore('idolAbandoned', scoringRubric.idolAbandoned, scores.idolAbandoned);
        addScore('shotInDarkWin', scoringRubric.shotInDarkWin, scores.shotInDarkWin);
        if (scores.coinFlipWin) addScore('coinFlipWin', scoringRubric.coinFlipWin, scores.coinFlipWin);

        if (scores.finalPlacement && scoringRubric.finalPlacement[scores.finalPlacement as keyof typeof scoringRubric.finalPlacement]) {
          const finalPoints = isEp1 ? 0 : scoringRubric.finalPlacement[scores.finalPlacement as keyof typeof scoringRubric.finalPlacement];
          episodeTotal += finalPoints;
          episodeBreakdown['finalPlacement'] = finalPoints;
          finalPlacement = scores.finalPlacement;
        }

        if (Object.keys(episodeBreakdown).length > 0) {
          breakdown[episode] = episodeBreakdown;
        }
        totalScore += episodeTotal;

        if (scores.votedOut && !votedOutEpisode) {
          votedOutEpisode = episodeNum;
        }
        if (scores.involuntaryEvacuation && !involuntaryEvacuationEpisode) {
          involuntaryEvacuationEpisode = episodeNum;
        }
        if (scores.voluntaryExit && !voluntaryExitEpisode) {
          voluntaryExitEpisode = episodeNum;
        }
      }
    });

    const finalIdols = allInventory[castaway.id]?.idols !== undefined && allInventory[castaway.id]?.idols > 0
      ? allInventory[castaway.id].idols
      : Math.max(0, dynamicIdols);

    const finalAdvantages = allInventory[castaway.id]?.advantages !== undefined && allInventory[castaway.id]?.advantages > 0
      ? allInventory[castaway.id].advantages
      : Math.max(0, dynamicAdvantages);

    return {
      id: castaway.id,
      name: castaway.name,
      imageUrl: castaway.imageUrl,
      totalScore,
      breakdown,
      episodeNotes,
      votedOutEpisode,
      involuntaryEvacuationEpisode,
      voluntaryExitEpisode,
      finalPlacement,
      inventory: {
        idols: finalIdols,
        advantages: finalAdvantages
      },
      isDrafted: userTeamSet.has(castaway.id),
    };

  }).sort((a, b) => {
    if (a.isDrafted && !b.isDrafted) return -1;
    if (!a.isDrafted && b.isDrafted) return 1;
    return b.totalScore - a.totalScore;
  });

  // Add points for correct elimination picks
  if (userInfo && eliminationPicks[userInfo.userName]) {
    Object.entries(eliminationPicks[userInfo.userName]).forEach(([episode, pickedCastawayId]) => {
      const episodeScores = allScores[episode];
      if (episodeScores) {
        const isPickedVotedOut = Object.keys(episodeScores).some(castawayId => 
          episodeScores[castawayId].votedOut && Number(castawayId) === pickedCastawayId
        );
        if (isPickedVotedOut) {
          const summary = summaries.find(s => s.id === pickedCastawayId);
          if (summary) {
            summary.totalScore += 3;
            if (!summary.breakdown[episode]) {
              summary.breakdown[episode] = {};
            }
            summary.breakdown[episode]['eliminationPick'] = 3;
          }
        }
      }
    });
  }

  return summaries;
};

const SurvivorPointTotals: React.FC = () => {
  const [allCastaways, setAllCastaways] = useState<Castaway[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [castawaySummaries, setCastawaySummaries] = useState<CastawayScoreSummary[]>([]);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [selectedSeason, setSelectedSeason] = useState<number>(() => {
    return parseInt(localStorage.getItem('selectedSeason') || '49');
  });

  useEffect(() => {
    const handleSeasonChange = () => {
      setSelectedSeason(parseInt(localStorage.getItem('selectedSeason') || '49'));
    };
    window.addEventListener('seasonChanged', handleSeasonChange);
    return () => window.removeEventListener('seasonChanged', handleSeasonChange);
  }, []);

  useEffect(() => {
    if (userInfo) {
      const loadCastaways = async () => {
        setIsLoading(true);
        try {
          const castaways = await getCastaways(userInfo.leagueId);
          setAllCastaways(castaways);
        } catch (err) {
          setError('Could not load castaway data.');
        } finally {
          setIsLoading(false);
        }
      };
      loadCastaways();
    }
  }, [userInfo]);

  useEffect(() => {
    const season = localStorage.getItem('selectedSeason') || '49';
    const storedUser = localStorage.getItem(`fantasyUser_${season}`);
    if (storedUser) {
      try {
        setUserInfo(JSON.parse(storedUser));
      } catch (e) {
        // Handle error if JSON is malformed
      }
    }
  }, [selectedSeason]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [scoresResponse, inventoryResponse, draftsResponse, eliminationPicksResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/scores/${selectedSeason}`),
          fetch(`${process.env.REACT_APP_API_URL}/api/inventory`),
          userInfo ? fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${userInfo.leagueId}/drafts`) : Promise.resolve(null),
          userInfo ? fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${userInfo.leagueId}/elimination-picks`) : Promise.resolve(null)
        ]);
        
        if (!scoresResponse.ok) throw new Error('Failed to fetch scores');
        if (!inventoryResponse.ok) throw new Error('Failed to fetch inventory');

        const scoresData = await scoresResponse.json();
        const inventoryData = await inventoryResponse.json();

        let currentTeam: number[] = [];
        if (draftsResponse && draftsResponse.ok && userInfo) {
          const draftsData = await draftsResponse.json();
          currentTeam = draftsData.drafts?.[userInfo.userName]?.team || [];
        }

        let eliminationPicksData: { eliminationPicks: { [userName: string]: { [episode: string]: number } } } = { eliminationPicks: {} };
        if (eliminationPicksResponse && eliminationPicksResponse.ok) {
          eliminationPicksData = await eliminationPicksResponse.json();
        }

        const summaries = calculateCastawayScores(
          allCastaways,
          scoresData.scores || {},
          inventoryData.inventory || {},
          currentTeam,
          eliminationPicksData.eliminationPicks,
          userInfo
        );
        setCastawaySummaries(summaries);
      } catch (err: any) {
        setError(err.message || 'An error occurred');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [userInfo, allCastaways, selectedSeason]);

  const formatCategoryName = (camelCase: string) => {
    return camelCase.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  };

  return (
    <Container className="mt-5 mb-5">
      <Card className="shadow-sm home-card">
        <Card.Header as="h4" className="card-title-custom text-center">
          Survivor Point Totals - Season {selectedSeason} {userInfo ? `(${userInfo.survivorName || userInfo.userName})` : ''}
        </Card.Header>
        <Card.Body>
          {isLoading && <div className="text-center"><Spinner animation="border" /></div>}
          {error && <Alert variant="danger">{error}</Alert>}
          {!isLoading && !error && (
            <Accordion defaultActiveKey={userInfo ? castawaySummaries.filter(c => c.isDrafted).map((c, i) => String(i)) : ['0']}>
              {castawaySummaries.map((castaway, index) => (
                <Accordion.Item eventKey={String(index)} key={castaway.id} className={castaway.isDrafted ? 'bg-light' : ''}>
                  <Accordion.Header>
                    <div style={{ position: 'relative', display: 'inline-block', marginRight: '10px' }}>
                      <Image src={castaway.imageUrl} roundedCircle style={{ width: '40px', height: '40px', objectFit: 'cover' }} />
                      {castaway.finalPlacement > 0 && (
                        <Badge 
                          bg={castaway.finalPlacement === 1 ? 'warning' : castaway.finalPlacement === 2 ? 'secondary' : 'danger'} 
                          text={castaway.finalPlacement === 1 ? 'dark' : 'white'}
                          style={{ 
                            position: 'absolute', 
                            top: -5, 
                            left: -5, 
                            fontSize: '0.6rem',
                            border: castaway.finalPlacement === 1 ? '1px solid gold' : castaway.finalPlacement === 2 ? '1px solid silver' : '1px solid #cd7f32',
                            backgroundColor: castaway.finalPlacement === 3 ? '#cd7f32' : undefined
                          }}
                        >
                          {castaway.finalPlacement === 1 ? '1st' : castaway.finalPlacement === 2 ? '2nd' : '3rd'}
                        </Badge>
                      )}
                    </div>
                    {castaway.name} - {castaway.totalScore} Points
                    {castaway.isDrafted && <Badge bg="success" className="ms-2">Your Pick</Badge>}
                    <Badge bg="info" className="ms-2">Idols: {castaway.inventory.idols}</Badge>
                    <Badge bg="warning" className="ms-2">Adv: {castaway.inventory.advantages}</Badge>
                    {castaway.votedOutEpisode && <Badge bg="danger" className="ms-2">Voted Out Ep {castaway.votedOutEpisode}</Badge>}
                    {castaway.involuntaryEvacuationEpisode && <Badge bg="primary" className="ms-2">Involuntary Evacuation Ep {castaway.involuntaryEvacuationEpisode}</Badge>}
                    {castaway.voluntaryExitEpisode && <Badge bg="dark" className="ms-2" style={{ backgroundColor: '#6b21a8' }}>Voluntary Exit Ep {castaway.voluntaryExitEpisode}</Badge>}
                  </Accordion.Header>
                  <Accordion.Body>
                    <h6>Current Inventory:</h6>
                    <ul>
                      <li>Idols: {castaway.inventory.idols}</li>
                      <li>Advantages: {castaway.inventory.advantages}</li>
                    </ul>
                    <h6>Score Breakdown by Episode:</h6>
                    {(() => {
                      const recordedEpisodes = Array.from(new Set([
                        ...Object.keys(castaway.breakdown),
                        ...Object.keys(castaway.episodeNotes || {})
                      ])).sort((a, b) => Number(a) - Number(b));

                      if (recordedEpisodes.length === 0) {
                        return <p className="text-muted">No scoring events recorded.</p>;
                      }

                      return (
                        <div className="table-responsive">
                          <Table striped bordered size="sm" className="align-middle">
                            <thead>
                              <tr>
                                <th style={{ width: '85px' }}>Episode</th>
                                <th>Category / Details</th>
                                <th style={{ width: '80px', textAlign: 'center' }}>Points</th>
                              </tr>
                            </thead>
                            <tbody>
                              {recordedEpisodes.map(episode => {
                                const categories = castaway.breakdown[episode] || {};
                                const catEntries = Object.entries(categories);
                                const note = castaway.episodeNotes?.[episode];
                                const rowCount = Math.max(1, catEntries.length) + (note ? 1 : 0);

                                return (
                                  <React.Fragment key={episode}>
                                    {catEntries.length > 0 ? (
                                      catEntries.map(([category, points], idx) => (
                                        <tr key={`${episode}-${category}`}>
                                          {idx === 0 && (
                                            <td rowSpan={rowCount} className="fw-bold align-middle bg-light text-center">
                                              Ep {episode}
                                            </td>
                                          )}
                                          <td>{formatCategoryName(category)}</td>
                                          <td className="text-center fw-semibold">{points > 0 ? `+${points}` : points}</td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td rowSpan={rowCount} className="fw-bold align-middle bg-light text-center">
                                          Ep {episode}
                                        </td>
                                        <td className="text-muted fst-italic">No fantasy points awarded</td>
                                        <td className="text-center text-muted">0</td>
                                      </tr>
                                    )}
                                    {note && (
                                      <tr style={{ backgroundColor: '#f0f9ff' }}>
                                        <td colSpan={2} style={{ fontSize: '0.82rem', color: '#495057' }}>
                                          <div className="d-flex align-items-start gap-1">
                                            <span style={{ fontSize: '1rem', lineHeight: '1.2' }}>🤖</span>
                                            <span><strong>Episode Summary:</strong> {note}</span>
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </Table>
                        </div>
                      );
                    })()}
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          )}
          <div className="text-center mt-4">
            <Link to="/" className="btn btn-secondary">Back to Home</Link>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default SurvivorPointTotals;
